import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SETTINGS_ROUTE, currentNavGroup, navGroupIsOpen, navRendering, railRows, t, type NavItemRow, type ResolvedNav } from '@bms2/shared';
import { Icon } from '../components/Icon';
import { CountBadge } from '../components/composites/CountBadge';
import styles from './shell.module.css';

/** 🔒 Decision 3072: a menu entry's count balloon, keyed by the entry's own key, with the words a screen reader hears. */
export type MenuCounts = Readonly<Record<string, { count: number; label: string }>>;

/**
 * The desktop Global Navigation Bar (piece 12, decision 0120). 🔨 Collapsing is
 * a distinct rendering mode, not a CSS width transition on the same markup: the
 * rail drops the group structure entirely and renders icon-only rows. An item
 * the viewer cannot open never reaches this component — the resolver hid it —
 * and a group with nothing under it renders nothing at all.
 */
export interface NavigationBarProps {
  nav: ResolvedNav;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /**
   * 🔒 The sections the person folded or opened by hand, from their own row in
   * the preference store (decision 1441; T692). A section it does not name opens
   * where it holds the page being read and folds otherwise.
   */
  groups?: Readonly<Record<string, boolean>>;
  onGroupsChange?: (groups: Readonly<Record<string, boolean>>) => void;
  /** Whether the foot of the bar offers Settings; an external session reaches no settings screen (decision 0607). */
  showSettings?: boolean;
  /** 🔒 Decision 3072 (T2183): the balloons beside the Dispatch Board, My Jobs and Approvals entries. */
  counts?: MenuCounts;
}

/**
 * 🔨 Every item draws its own icon, named on its row (piece 12). 💰 The bar drew
 * one glyph on every row — the chevron a list row uses to open a record — so the
 * collapsed rail was a column of identical arrows (found while building,
 * 2026-09-09).
 */
/**
 * 🔒 Whether a row whose route carries a query is the page being read (decision
 * 0762; T123). Payments received and Payments made are one path with two
 * filters, so the path alone would mark both; the row is current only where
 * every parameter it names is the address's own.
 */
export function queryRouteIsCurrent(route: string, pathname: string, search: string): boolean {
  const [path = '', query = ''] = route.split('?');
  if (pathname !== path && !pathname.startsWith(`${path}/`)) return false;
  const asked = new URLSearchParams(query);
  const held = new URLSearchParams(search);
  return [...asked.entries()].every(([key, value]) => held.get(key) === value);
}

function NavRow({ item, showLabel, badge }: { item: NavItemRow; showLabel: boolean; badge?: { count: number; label: string } | undefined }) {
  const location = useLocation();
  // 🔒 Decision 3072: the entry's balloon after its label, or on the icon's corner in the rail; a count of zero draws nothing.
  const balloon = badge && badge.count > 0 ? <span className={showLabel ? styles.navBadge : styles.navRailBadge}><CountBadge count={badge.count} label={badge.label} /></span> : null;
  if (item.route.includes('?')) {
    const current = queryRouteIsCurrent(item.route, location.pathname, location.search);
    return (
      <Link to={item.route} className={styles.navRow!} {...(showLabel ? {} : { title: item.label })} data-nav-item={item.key} {...(current ? { 'aria-current': 'page' as const } : {})}>
        <Icon meaning={item.icon ?? 'navigateInto'} size="nav" />
        {showLabel && <span>{item.label}</span>}
        {balloon}
      </Link>
    );
  }
  return (
    <NavLink to={item.route} className={styles.navRow!} {...(showLabel ? {} : { title: item.label })} data-nav-item={item.key} end={item.route === '/'}>
      <Icon meaning={item.icon ?? 'navigateInto'} size="nav" />
      {showLabel && <span>{item.label}</span>}
      {balloon}
    </NavLink>
  );
}

export function NavigationBar({ nav, collapsed, onCollapsedChange, groups, onGroupsChange, showSettings = true, counts = {} }: NavigationBarProps) {
  const rendering = navRendering(collapsed);
  const location = useLocation();
  // 🔨 A choice made here draws at once, ahead of the write; the frame's answer seeds it and a later answer does not undo a press.
  const [chosen, setChosen] = useState<Readonly<Record<string, boolean>> | null>(null);
  const own = chosen ?? groups ?? {};
  const current = currentNavGroup(nav, location.pathname);
  const groupIsOpen = (key: string) => navGroupIsOpen(key, own, current, nav.unfolded === true);
  const toggleGroup = (key: string) => {
    const next = { ...own, [key]: !groupIsOpen(key) };
    setChosen(next);
    onGroupsChange?.(next);
  };

  return (
    <nav className={styles.nav} data-rendering={rendering} data-shell="nav" aria-label={t('shell.navigation')}>
      {rendering === 'rail' ? (
        railRows(nav).map((item) => <NavRow key={item.key} item={item} showLabel={false} badge={counts[item.key]} />)
      ) : (
        <>
          {nav.pinned.map((item) => <NavRow key={item.key} item={item} showLabel badge={counts[item.key]} />)}
          {nav.groups.map((group) => (
            <section key={group.key} data-nav-group={group.key}>
              <button
                type="button"
                className={styles.navGroupHead}
                aria-expanded={groupIsOpen(group.key)}
                onClick={() => toggleGroup(group.key)}
              >
                {group.label}
                <span className={styles.navGroupChevron}>
                  <Icon meaning={groupIsOpen(group.key) ? 'collapse' : 'expand'} size="nav" />
                </span>
              </button>
              {groupIsOpen(group.key) && group.items.map((item) => <NavRow key={item.key} item={item} showLabel badge={counts[item.key]} />)}
            </section>
          ))}
        </>
      )}
      {/* 🔒 The foot of the bar: Settings beside Collapse, each a row shaped like the entries above it (decision 1441; T692). The header's gear stays. */}
      <div className={styles.navFooter}>
        {showSettings ? (
          <NavLink to={SETTINGS_ROUTE} className={styles.navRow!} {...(collapsed ? { title: t('shell.settings') } : {})} data-shell="nav-settings">
            <Icon meaning="settings" size="nav" />
            {!collapsed && <span>{t('shell.settings')}</span>}
          </NavLink>
        ) : null}
        <button type="button" className={styles.navCollapse} onClick={() => onCollapsedChange(!collapsed)} data-shell="nav-collapse" aria-label={collapsed ? t('shell.expandNav') : t('shell.collapseNav')} {...(collapsed ? { title: t('shell.expandNav') } : {})}>
          <Icon meaning={collapsed ? 'next' : 'previous'} size="nav" />
          {!collapsed && <span>{t('shell.collapse')}</span>}
        </button>
      </div>
    </nav>
  );
}

/**
 * The phone drawer, below the md breakpoint. It reuses the same nav data and
 * groups and keeps the same active-item and group-expand semantics; a bottom
 * tab bar is not used, because twelve-plus items across six groups map onto a
 * drawer's group-and-expand semantics and not onto tabs.
 */
export interface NavigationDrawerProps {
  nav: ResolvedNav;
  open: boolean;
  onClose: () => void;
  /** 🔒 Decision 3072: the same balloons the bar draws. */
  counts?: MenuCounts;
}

export function NavigationDrawer({ nav, open, onClose, counts = {} }: NavigationDrawerProps) {
  // 🔨 The drawer is an off-canvas panel, not a modal: ModalShell is the one
  // construction in the application that renders a dialog element (piece 5), and
  // the drawer would be a second. It keeps the behaviour that matters — a scrim,
  // Escape, and focus moved inside — without claiming the top layer.
  const panel = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <button type="button" className={styles.scrim} aria-label={t('shell.closeMenu')} onClick={onClose} data-shell="drawer-scrim" />
      <aside className={styles.drawer} ref={panel} tabIndex={-1} data-shell="drawer" aria-label={t('shell.navigation')}>
        <div className={styles.drawerBody}>
          <button type="button" className={styles.headerButton} onClick={onClose} aria-label={t('shell.closeMenu')}>
            <Icon meaning="closeModal" size="header" />
          </button>
          {nav.pinned.map((item) => <NavRow key={item.key} item={item} showLabel badge={counts[item.key]} />)}
          {nav.groups.map((group) => (
            <section key={group.key} data-nav-group={group.key}>
              <span className={styles.navGroupHead}>{group.label}</span>
              {group.items.map((item) => <NavRow key={item.key} item={item} showLabel badge={counts[item.key]} />)}
            </section>
          ))}
        </div>
      </aside>
    </>
  );
}
